import { toast } from '@ethsign/ui';
import localforage from 'localforage';

import { useRouter } from '@/hooks';

import {
  completeContract,
  downloadContract,
  exportRecipients,
  hideContracts,
  moveContracts,
  queryContracts,
  queryFolders,
  voidContract
} from '@/services';
import { ContractItem, Contracts, ContractsQuery, FolderItem, ICurrentUser } from '@/types';
import { dateFormat, downloadContractFile, reportGAEvent } from '@/utils';
import { ControllerBase } from '@/utils/bizify';

type PageData = {
  openVoidModal: boolean;
  openDeactivateModal: boolean;
  openHideModal: boolean;
  openMoveModal: boolean;
  openDetailModal: boolean;
  openInviteViewersModal: boolean;
  checkedFolder: FolderItem;
  selectedContracts: any[];
  folders: { rows: FolderItem[]; totalContract?: number };
  contracts?: Contracts;
  loading?: boolean;
  contractsLoading?: boolean;
  passwordModalOpen: boolean;
  password?: string;
  passwordModalType?: 'download' | 'view' | 'share';
  currentContract: ContractItem;
  contractsQuery: ContractsQuery;
  topContracts: ContractItem[] | undefined;
  shareModalOpen?: boolean;
  shareType?: 'signer' | 'viewer';
};
export class PageCtrl extends ControllerBase<PageData> {
  protected $data(): PageData {
    return {
      openVoidModal: false,
      openDeactivateModal: false,
      openDetailModal: false,
      openHideModal: false,
      openMoveModal: false,
      checkedFolder: { folderName: '' },
      selectedContracts: [],
      folders: { rows: [], totalContract: 0 },
      contracts: {},
      loading: false,
      contractsLoading: false,
      passwordModalOpen: false,
      password: '',
      currentContract: {} as ContractItem,
      contractsQuery: {},
      topContracts: [],
      shareModalOpen: false,
      shareType: undefined,
      openInviteViewersModal: false
    };
  }

  setVoidModalOpen = (open: boolean): void => {
    this.state.openVoidModal = open;
  };
  setPasswordModalType = (passwordModalType: 'download' | 'view' | 'share'): void => {
    this.state.passwordModalType = passwordModalType;
  };
  setShareType = (shareType: 'signer' | 'viewer'): void => {
    this.state.shareType = shareType;
  };
  setOpenDeactivateModal = (open: boolean): void => {
    this.state.openDeactivateModal = open;
  };
  setOpenHideModal = (open: boolean): void => {
    this.state.openHideModal = open;
    if (!open) {
      this.state.selectedContracts = [];
    }
  };
  setOpenMoveModal = (open: boolean): void => {
    this.state.openMoveModal = open;
    if (!open) {
      this.state.selectedContracts = [];
    }
  };

  setOpenDetailModal = (open: boolean): void => {
    this.state.openDetailModal = open;
    if (!open) {
      this.state.password = '';
    }
  };
  onMove = (data: any[]): void => {
    this.state.selectedContracts = data;
    this.state.openMoveModal = true;
    this.state.checkedFolder = { folderName: 'All Contracts' };
  };
  onHide = (data: any[]): void => {
    this.state.selectedContracts = data;
    this.state.openHideModal = true;
  };
  setCheckedFolder = (checkedFolder: FolderItem): void => {
    this.state.checkedFolder = checkedFolder;
  };
  getFolders = (): void => {
    queryFolders().then((res) => {
      this.state.folders = res;
    });
  };
  getTopContracts = ({ folder }: { folder: string | undefined }): void => {
    queryContracts({ folder, page: 1, size: 4, hidden: false }).then((res) => {
      if (res.data?.length === 4) {
        this.state.topContracts = res.data.slice(0, 3);
      } else {
        this.state.topContracts = [];
      }
    });
  };
  getContracts = ({ folder, filter, page, keyword, contractType }: ContractsQuery): void => {
    this.state.contractsLoading = true;
    queryContracts({ folder, filter, page, keyword, contractType, hidden: false })
      .then((res) => {
        this.state.contracts = res;
        this.state.contractsLoading = false;
      })
      .catch(() => {
        this.state.contractsLoading = false;
      });
  };
  onMoveConfirm = (folderId: string | undefined): void => {
    const { checkedFolder } = this.state;
    if (checkedFolder.folderName) {
      const ids = this.state.selectedContracts.map((item) => item.id);
      const len = ids.length;
      this.state.loading = true;
      moveContracts({ folderId: checkedFolder.id, contracts: ids })
        .then(() => {
          this.getContracts({ folder: folderId, page: 1 });
          this.getTopContracts({ folder: folderId });
          this.getFolders();
          this.state.loading = false;
          this.state.selectedContracts = [];
          toast({
            variant: 'success',
            title: 'Success',
            description: `${len > 1 ? len + ' Contracts' : 'Contract'} moved to folder ${checkedFolder.folderName}`
          });
          this.state.openMoveModal = false;
          this.state.checkedFolder = {};
        })
        .catch(() => {
          this.state.loading = false;
        });
    }
  };
  onHideConfirm = (folderId: string | undefined): void => {
    const ids = this.state.selectedContracts.map((item) => item.id);
    this.state.loading = true;
    hideContracts({ contracts: ids })
      .then(() => {
        this.getContracts({ folder: folderId, page: 1 });
        this.getTopContracts({ folder: folderId });
        this.getFolders();
        this.state.loading = false;
        this.state.openHideModal = false;
        this.state.selectedContracts = [];
        toast({
          variant: 'success',
          title: 'Success',
          description: `Contract${this.state.selectedContracts.length > 1 ? 's' : ''} has been hidden`
        });
      })
      .catch(() => {
        this.state.loading = false;
      });
  };
  setPassWordModalOpen = (passwordModalOpen: boolean): void => {
    this.state.passwordModalOpen = passwordModalOpen;
    const passwordModalType = this.state.passwordModalType;
    if (!passwordModalOpen) {
      if (passwordModalType === 'view' || passwordModalType === 'share') {
        this.state.passwordModalType = undefined;
      } else {
        this.state.password = '';
      }
    }
  };
  setPassword = (password: string): void => {
    this.state.password = password;
  };
  setCurrentContract = (currentContract: ContractItem): void => {
    this.state.currentContract = currentContract;
  };

  onDownload = async (data: ContractItem, password?: string): Promise<boolean> => {
    this.state.contractsLoading = true;
    try {
      const contractData = await downloadContract({ id: data.id });
      const { recipients, fileContent, contractType } = contractData;

      return await downloadContractFile({
        recipients,
        contractType,
        fileContent,
        password,
        data
      });
    } catch (e) {
      console.error(e);
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Download failed'
      });
      return false;
    } finally {
      this.state.contractsLoading = false;
    }
  };
  setLoading = (loading: boolean): void => {
    this.state.loading = loading;
  };
  onUpload = async (
    file: File[],
    folderId: string | undefined,
    router: ReturnType<typeof useRouter>,
    user: ICurrentUser
  ): Promise<void> => {
    const id = `temp${Date.now()}`;
    localforage.config({
      driver: localforage.INDEXEDDB
    });
    await localforage.setItem(id, file);
    reportGAEvent('pc_contract_uploadContract', {
      address: user.address,
      fileName: file?.[0]?.name,
      tempId: id
    });
    router.push(`/prepare/${id}${folderId ? `?folderId=${folderId}` : ''}`);
  };
  showPassword = (isShow: boolean): void => {
    if (isShow) {
      this.state.passwordModalOpen = true;
      this.state.passwordModalType = 'view';
    } else {
      this.state.password = '';
    }
  };
  deActiveContract = async (id: string, folderId?: string): Promise<void> => {
    const result = await completeContract({ id });
    if (result) {
      this.getContracts({ folder: folderId, page: 1 });
      this.getTopContracts({ folder: folderId });
      toast({
        variant: 'success',
        title: 'Success',
        description: 'Your have successfully deactivated the signing link of this contract'
      });
      this.setOpenDeactivateModal(false);
    }
  };
  setShareModalOpen = (open: boolean): void => {
    this.state.shareModalOpen = open;
  };
  onDecryptConfirm = async (v: string): Promise<boolean> => {
    const passwordModalType = this.state.passwordModalType;
    if (passwordModalType == 'view') {
      this.setPassword(v);
      this.setPassWordModalOpen(false);
      return true;
    } else if (passwordModalType == 'share') {
      this.setPassword(v);
      this.setPassWordModalOpen(false);
      this.setShareModalOpen(true);
      return true;
    } else {
      const result = await this.onDownload(this.state.currentContract, v);
      this.setPassword('');
      if (result) {
        this.setPassWordModalOpen(false);
        return true;
      } else {
        return false;
      }
    }
  };
  exportSigners = async (id: string, title: string): Promise<void> => {
    const res = await exportRecipients(id);

    // 将数组转换为二维数组
    const data = res.rows;
    const headers = ['chainType', 'address', 'nickName', 'signingAt', 'email'];
    const rows = data.map((obj: any) => {
      const res: any = [];
      Object.keys(obj).forEach((key) => {
        if (key === 'signingAt') {
          res.push(dateFormat(Number(obj[key]), 'YYYY-MM-DD HH:mm:ss ZZ'));
        } else if (headers.includes(key)) {
          res.push(obj[key]);
        }
      });
      return res;
    });

    rows.unshift(headers);
    const csvString = rows.map((row: any) => row.join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = title + '.csv';
    document.body.appendChild(link);
    link.click();

    URL.revokeObjectURL(url);
  };
  setInviteViewersModal = (open: boolean): void => {
    this.state.openInviteViewersModal = open;
  };
  voidContract = async (id: string, folderId?: string): Promise<void> => {
    const result = await voidContract({ id });
    if (result) {
      this.getContracts({ folder: folderId, page: 1 });
      this.getTopContracts({ folder: folderId });
      toast({
        variant: 'success',
        title: 'Success',
        description: 'Your have successfully voided this contract'
      });
      this.setVoidModalOpen(false);
    }
  };
}
